import { type JSX } from 'react';
import { Link } from 'react-router-dom';
import { Clock } from 'lucide-react';
import { freePeriodLapseConsequence } from '../lib/trial.ts';

/**
 * THE FREE RUN ENDS, AND SAYS SO FIRST. Once it lapses the practice can no longer take a
 * new client (`caseSlotAvailable`), so arriving at that wall unwarned is the same defect
 * as founding without being told the capacity. Warning only — it decides no access.
 *
 * Staff-only by construction: `trialEndsAt` is null for everyone else (me.service.ts).
 */

export function TrialEndingBanner({
  practiceId,
  practiceName,
  daysRemaining,
  heldSlots,
  linkTo,
}: {
  readonly practiceId: string;
  /** Named, because a member of two practices gets one banner each. */
  readonly practiceName: string;
  readonly daysRemaining: number;
  /** Cases + agreements out for signature held on this practice's plan — see trial.ts. */
  readonly heldSlots: number;
  linkTo?: string;
}): JSX.Element {
  const lapsed = daysRemaining < 0;
  return (
    <div
      role="status"
      data-testid="trial-ending-banner"
      className="flex flex-wrap items-center justify-between gap-3 border-b border-primary/30 bg-primary/10 px-4 py-2 text-sm text-foreground"
    >
      <div className="flex items-center gap-2">
        <Clock className="h-4 w-4 shrink-0 text-primary" aria-hidden="true" />
        <span>
          {lapsed
            ? `The free period for ${practiceName} has ended. You can carry on with your current clients; taking on a new one needs a plan.`
              + freePeriodLapseConsequence(daysRemaining, heldSlots)
            : daysRemaining === 0
              ? `The free period for ${practiceName} ends today. After that you can carry on with your current clients, but taking on a new one needs a plan.`
              : `The free period for ${practiceName} ends in ${String(daysRemaining)} ${daysRemaining === 1 ? 'day' : 'days'}. After that you can carry on with your current clients, but taking on a new one needs a plan.`}
        </span>
      </div>
      <Link
        to={linkTo ?? `/practices/${practiceId}/plan`}
        data-testid="trial-ending-banner-link"
        className="font-medium text-primary hover:underline"
      >
        See plans
      </Link>
    </div>
  );
}
